import InfiniteScroll from '../_app/cuchillo/scroll/InfiniteScroll';
import { Scroll } from '../_app/cuchillo/scroll/Scroll';
import { gsap, Power2 } from "gsap";
import { Maths } from '../_app/cuchillo/utils/Maths';
import { CSS } from '../_app/cuchillo/utils/CSS';
import { GetBy, C } from '../_app/cuchillo/core/Element';
import InfiniteScroll__Item from '../_app/cuchillo/scroll/InfiniteScroll__Item';
import { isSmartphone } from '../_app/cuchillo/core/Basics';
import { Metrics } from '../_app/cuchillo/core/Metrics';
import BG from '../_app/cuchillo/layout/Background';
import ScrollItem__BlockDefault from './ScrollItem__BlockDefault';

const DEFAULT_CIRCLE = {
    angle: 52,
    scaleMin: 0.5,
    infoShowAt: 0.06,
    paletteDelay: 180,
    modScaleFast: 0.9,
    modAngleFast: 1.2,
    modSpeedMin: 0,
    modSpeedRef: 20,
};

const CHROME_FADE = 0.2;
const CHROME_SHIFT = 28; // px — slight vertical travel on swap
const MOD_SMOOTH = 0.85;

function readCircleVars(__el) {
    const s = getComputedStyle(__el);
    const diameterRaw = s.getPropertyValue('--circle-diameter').trim();
    const diameter = Metrics.parseSize(diameterRaw);
    const shiftRaw = s.getPropertyValue('--chrome-shift').trim();
    const shift = Metrics.parseSize(shiftRaw);

    return {
        diameter: diameter > 0 ? diameter : Metrics.WIDTH * 2,
        angle: parseFloat(s.getPropertyValue('--circle-angle')) || DEFAULT_CIRCLE.angle,
        scaleMin: parseFloat(s.getPropertyValue('--scale-min')) || DEFAULT_CIRCLE.scaleMin,
        infoShowAt: parseFloat(s.getPropertyValue('--info-show-at')) || DEFAULT_CIRCLE.infoShowAt,
        paletteDelay: parseFloat(s.getPropertyValue('--palette-delay')) || DEFAULT_CIRCLE.paletteDelay,
        chromeShift: shift > 0 ? shift : CHROME_SHIFT,
        modScaleFast: parseFloat(s.getPropertyValue('--mod-scale-fast')) || DEFAULT_CIRCLE.modScaleFast,
        modAngleFast: parseFloat(s.getPropertyValue('--mod-angle-fast')) || DEFAULT_CIRCLE.modAngleFast,
        modSpeedMin: parseFloat(s.getPropertyValue('--mod-speed-min')) || DEFAULT_CIRCLE.modSpeedMin,
        modSpeedRef: parseFloat(s.getPropertyValue('--mod-speed-ref')) || DEFAULT_CIRCLE.modSpeedRef,
    };
}

/**
 * Fixed left/right panels.
 * Stays visible while cards orbit; only swaps when a new item becomes active.
 * Exit never cancelled once started; superseded enters are dropped (never appear).
 * Scroll down → exit up / enter from below; reverse the other way.
 */
class WidgetReportsChrome {
    constructor(__section) {
        this._section = __section;
        this._subtitle = GetBy.selector('[data-chrome-subtitle]', __section)[0];
        this._title = GetBy.selector('[data-chrome-title]', __section)[0];
        this._abstract = GetBy.selector('[data-chrome-abstract]', __section)[0];
        this._chapters = GetBy.selector('[data-chrome-chapters]', __section)[0];
        this._soon = GetBy.selector('[data-chrome-soon]', __section)[0];
        this._chaptersTitle = GetBy.selector('[data-chrome-chapter-title]', __section)[0];
        this._actions = GetBy.selector('[data-chrome-actions]', __section)[0];
        this._see = GetBy.selector('[data-chrome-see]', __section)[0];
        this._pdf = GetBy.selector('[data-chrome-pdf]', __section)[0];
        this._layers = [];

        C.forEach(GetBy.selector('[data-chrome-layer]', __section), (el) => {
            this._layers.push(el);
        });

        /** @type {'hidden'|'entering'|'visible'|'exiting'} */
        this._phase = 'hidden';
        this._active = null;
        this._activeKey = null;
        /** Latest show request waiting for an enter (after exit finishes) */
        this._pending = null;
        this._enterGen = 0;
        this._exitDir = 1;

        if (this._layers.length) {
            gsap.set(this._layers, { opacity: 0, y: 0 });
        }

        if (this._title) {
            this._title.addEventListener('click', (e) => {
                if (this._title.classList.contains('--disabled')) {
                    e.preventDefault();
                }
            });
        }
    }

    _dataKey(__data) {
        if (!__data) return '';
        return `${__data.link || ''}|${__data.title || ''}`;
    }

    _shift() {
        return this._section?._reportsCircle?.chromeShift || CHROME_SHIFT;
    }

    _normDir(__direction) {
        return __direction >= 0 ? 1 : -1;
    }

    /**
     * @param {*} __item active InfiniteScroll item
     * @param {object} __data report payload
     * @param {number} __direction InfiniteScroll direction
     */
    show(__item, __data, __direction = 1) {
        if (!this._layers.length || !__data) return;

        const key = this._dataKey(__data);
        const dir = Scroll.direction;
        const req = { item: __item, data: __data, key, dir };

        // Already showing / entering this content
        if (key && key === this._activeKey && (this._phase === 'visible' || this._phase === 'entering')) {
            this._active = __item;
            return;
        }

        // Always keep the latest desired content (cancels any previous pending enter)
        this._pending = req;

        if (this._phase === 'exiting') {
            // Exit must finish; pending enter will run after (or be replaced again)
            return;
        }

        if (this._phase === 'hidden') {
            this._pending = null;
            this._beginEnter(req);
            return;
        }

        if (this._phase === 'entering') {
            // Cancel enter only — then finish a full exit from current opacity
            this._cancelEnter();
            this._beginExit();
            return;
        }

        // visible → start exit (never interrupted once running)
        this._beginExit();
    }

    /** Soft hide when leaving the widget — cancel pending enter, finish exit */
    hide() {
        this._pending = null;
        this._active = null;
        this._activeKey = null;

        if (this._phase === 'hidden' || this._phase === 'exiting') {
            // If exiting, let it finish; nothing re-enters (_pending cleared)
            return;
        }

        if (this._phase === 'entering') {
            this._cancelEnter();
        }

        this._beginExit();
    }

    /** Invalidate / kill an in-flight enter only (never touch an exit) */
    _cancelEnter() {
        this._enterGen += 1;
        if (this._phase === 'entering') {
            gsap.killTweensOf(this._layers);
        }
    }

    _setWillChange(__on) {
        const v = __on ? 'transform, opacity' : 'auto';
        for (let i = 0; i < this._layers.length; i++) {
            this._layers[i].style.willChange = v;
        }
    }

    _beginExit() {
        if (this._phase === 'exiting') return;

        // Direction from the request that triggered this swap
        const dir = this._pending?.dir ?? this._exitDir;
        this._exitDir = dir;
        this._phase = 'exiting';

        const offset = this._shift();
        const exitY = dir * offset;

        this._setWillChange(true);
        // Do not killTweens — exit always runs to completion
        gsap.to(this._layers, {
            y: exitY,
            opacity: 0,
            duration: CHROME_FADE * 0.4,
            ease: Power2.easeOut,
            onComplete: () => {
                this._phase = 'hidden';
                this._activeKey = null;

                const next = this._pending;
                this._pending = null;

                if (next) this._beginEnter(next);
                else this._setWillChange(false);
            },
        });
    }

    _beginEnter(__req) {
        const gen = ++this._enterGen;
        this._phase = 'entering';
        this._active = __req.item;
        this._activeKey = __req.key;
        this._exitDir = __req.dir;

        this._fill(__req.data);

        const offset = this._shift();
        const enterY = -__req.dir * offset;

        this._setWillChange(true);
        gsap.fromTo(
            this._layers,
            { y: enterY, opacity: 0 },
            {
                y: 0,
                opacity: 1,
                duration: CHROME_FADE,
                ease: Power2.easeOut,
                onComplete: () => {
                    if (gen !== this._enterGen) return; // enter was cancelled
                    this._phase = 'visible';

                    if (this._pending && this._pending.key !== this._activeKey) {
                        this._beginExit();
                    } else {
                        this._pending = null;
                        this._setWillChange(false);
                    }
                },
            }
        );
    }

    _fill(__data) {
        const title = __data.title || '';
        const subtitle = __data.subtitle || '';
        const abstract = __data.abstract || '';
        const chapters = Array.isArray(__data.chapters) ? __data.chapters : [];
        const hasChapters = chapters.length > 0;

        if (this._subtitle) {
            this._subtitle.textContent = subtitle;
            this._subtitle.hidden = !subtitle;
        }

        if (this._title) {
            this._title.textContent = title;
            if (hasChapters && __data.link) {
                this._title.setAttribute('href', __data.link);
                this._title.removeAttribute('aria-disabled');
                this._title.classList.remove('--disabled');
            } else {
                this._title.setAttribute('href', '#');
                this._title.setAttribute('aria-disabled', 'true');
                this._title.classList.add('--disabled');
            }
        }

        if (this._abstract) {
            this._abstract.textContent = abstract;
        }

        if (this._chapters && this._soon) {
            this._chapters.innerHTML = '';
            if (hasChapters) {
                this._chapters.hidden = false;
                console.log(this._chaptersTitle);

                this._chaptersTitle.hidden = false;
                this._soon.hidden = true;
                chapters.forEach((ch) => {
                    const li = document.createElement('li');
                    li.textContent = (ch.title || ch.label || '').replace(/\r\n|\r|\n/g, '\n');
                    this._chapters.appendChild(li);
                });
            } else {
                this._chaptersTitle.hidden = true;
                this._soon.hidden = false;
            }
        }

        if (this._actions) {
            // Coming soon (no chapters) → no CTAs
            if (!hasChapters) {
                this._actions.hidden = true;
                if (this._see) this._see.setAttribute('href', '#');
                if (this._pdf) {
                    this._pdf.hidden = true;
                    this._pdf.setAttribute('href', '#');
                }
            } else {
                this._actions.hidden = false;
                if (this._see) {
                    this._see.setAttribute('href', __data.link || '#');
                }
                const pdfUrl = typeof __data.pdf === 'string'
                    ? __data.pdf
                    : (__data.pdf?.url || '');
                if (this._pdf) {
                    if (pdfUrl) {
                        this._pdf.hidden = false;
                        this._pdf.setAttribute('href', pdfUrl);
                    } else {
                        this._pdf.hidden = true;
                        this._pdf.setAttribute('href', '#');
                    }
                }
            }
        }
    }

    dispose() {
        this._enterGen += 1;
        this._pending = null;
        gsap.killTweensOf(this._layers);
        this._setWillChange(false);
        this._phase = 'hidden';
        this._active = null;
        this._activeKey = null;
    }
}


class ScrollItem__WidgetReports extends ScrollItem__BlockDefault {
    _call;
    _slider;
    _yOld = 0;
    _chrome;
    _modScale = 0;
    _modAngle = 0;
    _sectionLeft = 0;
    _poseY0 = 0;
    _needsPaint = true;

    /** Shared debounce for BG.changePalette across all items */
    static _paletteTimer = null;
    static _palettePending = null;

    static schedulePalette(__key, __delay) {
        if (!__key) return;
        // Same palette already pending or active → skip reschedule noise
        if (ScrollItem__WidgetReports._palettePending === __key && ScrollItem__WidgetReports._paletteTimer) return;
        if (BG.actualKey === __key && !ScrollItem__WidgetReports._paletteTimer) return;

        clearTimeout(ScrollItem__WidgetReports._paletteTimer);
        ScrollItem__WidgetReports._palettePending = __key;
        ScrollItem__WidgetReports._paletteTimer = setTimeout(() => {
            ScrollItem__WidgetReports._paletteTimer = null;
            ScrollItem__WidgetReports._palettePending = null;
            BG.changePalette(__key);
        }, Math.max(0, __delay || 0));
    }

    static cancelPalette() {
        clearTimeout(ScrollItem__WidgetReports._paletteTimer);
        ScrollItem__WidgetReports._paletteTimer = null;
        ScrollItem__WidgetReports._palettePending = null;
    }

    //==================================================================================================================
    //          CONSTRUCTOR
    //==================================================================================================================

    constructor(__link, __index, __scroller) {
        super(__link, __index, __scroller);

        if (!GetBy.class('__holder', __link)[0]) return;

        this.container = __link;
        this.holder = GetBy.class('__holder', this.container)[0];
        this.container._reportsMods = { scale: 0, angle: 0 };
        this._syncCircleVars();
        this._syncSectionLeft();
        this._updateSectionPose();
        this._chrome = new WidgetReportsChrome(this.container);
        this.container._reportsChrome = this._chrome;

        // Deco cards by list position (same index as main), not by report id
        const decoCards = [];
        C.forEach(GetBy.selector('.widget-reports__slider.--deco > li', this.container), (li) => {
            decoCards.push(GetBy.selector('.card', li)[0] || null);
        });
        this.container._reportsDecoCards = decoCards;

        this._slider = new InfiniteScroll(InfiniteScroll.AXIS_X, {
            domResize: this.container,
            appendContainer: this.holder,
            container: this.container,
            multiplicator: 2,
            minSpeed: 0,
            inverted: false,
            gap: 0,
            offset: this.container.offsetWidth * 0.35,
            itemClass: ScrollItem__WidgetReports__Item,
            hasVirtuaScroll: true,
            hasInteraction: true
        });

        this._call = () => this.loop();
        this.onShow = () => {
            this._needsPaint = true;
            this._syncCircleVars();
            this._syncSectionLeft();
            this._updateSectionPose();
            this._slider.show();
            this._slider.enabled = true;
            gsap.ticker.add(this._call);
        };

        this.onHide = () => {
            this._slider.enabled = false;
            gsap.ticker.remove(this._call);
            ScrollItem__WidgetReports.cancelPalette();
            if (this._chrome) this._chrome.hide();
        };
    }

    /** Skip Scroll_Item CSS vars — this block does not read --y / --progress* */
    draw() {
        if (this.onMove) {
            this.onMove({ x: 0, y: this.y, z: 0 }, { width: this.width, height: this.height });
        }
    }

    _syncCircleVars() {
        this.container._reportsCircle = readCircleVars(this.container);
        this._syncReportsFrame();
    }

    /** Layout read only on resize/show — left is stable while scrolling */
    _syncSectionLeft() {
        this._sectionLeft = this.container.getBoundingClientRect().left;
        this._poseY0 = this.y;
    }

    /** rect.top ≡ −this.y (Scroll_Item); ty = scroll delta since last layout sync */
    _updateSectionPose() {
        const shared = this.container._reportsSection || (this.container._reportsSection = {});
        shared.left = this._sectionLeft;
        shared.top = -this.y;
        shared.ty = this.y - this._poseY0;
        this._syncReportsFrame();
    }

    /** Shared circle + mods + effective angle + page ty for this tick */
    _syncReportsFrame() {
        const circle = this.container._reportsCircle || { ...DEFAULT_CIRCLE, diameter: Metrics.WIDTH * 2 };
        const mods = this.container._reportsMods || { scale: 0, angle: 0 };
        this.container._reportsFrame = {
            circle,
            mods,
            angleEff: circle.angle * Maths.lerp(1, circle.modAngleFast, mods.angle),
            ty: this.container._reportsSection?.ty || 0,
        };
    }

    /** Mods stay 0 until min speed; then ramp on (|speed| − min) / ref. JS only — no CSS vars. */
    _updateSpeedMods() {
        if (!this.container || !this._slider) return;

        const {
            modSpeedMin = DEFAULT_CIRCLE.modSpeedMin,
            modSpeedRef = DEFAULT_CIRCLE.modSpeedRef,
        } = this.container._reportsCircle || {};

        const excess = Math.abs(this._slider.speed) - modSpeedMin;
        const t = excess <= 0 ? 0 : Maths.clamp(excess / Math.max(modSpeedRef, 0.001), 0, 1);

        this._modScale = Maths.lerp(this._modScale, t, MOD_SMOOTH);
        this._modAngle = Maths.lerp(this._modAngle, t, MOD_SMOOTH);

        const mods = this.container._reportsMods || (this.container._reportsMods = { scale: 0, angle: 0 });
        mods.scale = this._modScale;
        mods.angle = this._modAngle;
    }

    resize(w, h) {
        super.resize(w, h);
        this._needsPaint = true;
        this._syncCircleVars();
        this._syncSectionLeft();
        this._updateSectionPose();
        if (this._slider) {
            this._slider.options.offset = this.container.offsetWidth * 0.35;
            this._slider.resize();
        }
    }

    loop() {
        if (!this._slider) return;

        const y = this.y;
        const deltaY = -(y - this._yOld) * 0.2;
        this._yOld = y;

        const movingPage = deltaY !== 0;
        if (movingPage) {
            this._slider.check({ deltaY });
            this._updateSectionPose();
        }

        this.container._reportsDirection = this._slider.direction;

        const slider = this._slider;
        const sliderIdle = !movingPage
            && Math.abs(slider.speed) < 0.01
            && Maths.precission(slider.target) === Maths.precission(slider.position);

        if (sliderIdle) {
            this._updateSpeedMods();
            this._syncReportsFrame();
            const mods = this.container._reportsMods;
            const modsIdle = !mods
                || (Math.abs(mods.scale) < 0.001 && Math.abs(mods.angle) < 0.001);
            if (modsIdle && !this._needsPaint) return;
            this._needsPaint = false;
            slider.loop();
            return;
        }

        this._needsPaint = false;
        this._syncReportsFrame();
        slider.loop();
        this._updateSpeedMods();
    }

    dispose() {
        if (this._slider) this._slider.dispose();
        if (this._chrome) this._chrome.dispose();
        ScrollItem__WidgetReports.cancelPalette();
        super.dispose();
    }
}

class ScrollItem__WidgetReports__Item extends InfiniteScroll__Item {
    _card;
    _cardDeco;
    _figure;
    _figureDeco;
    _limits = {}
    hasMove = false;
    _reportData = null;
    _cardW = 0;
    _cardH = 0;
    _pose = { left: '', top: '', originX: 0, originY: 0 };
    _lastLeft = null;
    _lastTop = null;
    _lastOrigin = null;
    _lastRotate = null;
    _lastRotateDeco = null;
    _lastScale = null;
    _lastZ = null;

    constructor(__d, __i, __axis = InfiniteScroll.AXIS_Y, __hasMove, __scroller) {
        super(__d, __i, __axis, false, __scroller);

        this._card = GetBy.selector('.card', __d)[0];
        this._figure = GetBy.selector('figure', this._card)[0];
        this._section = __d.closest('.widget-reports');
        this._palette = __d.getAttribute('data-report-palette') || 'primary';
        this.isInfoShow = false;
        this._reportData = this._parseReportData(__d);

        // Twin deco at the same list index (different report after rotateBy)
        this._cardDeco = this._section?._reportsDecoCards?.[this.indice] || null;
        this._figureDeco = this._cardDeco ? GetBy.selector('figure', this._cardDeco)[0] : null;

        this.setupLimits();
        this.resetHook();
    }

    _parseReportData(__d) {
        const script = GetBy.selector('script.__report-data', __d)[0];
        if (!script) return null;
        try {
            return JSON.parse(script.textContent);
        } catch {
            return null;
        }
    }

    _circle() {
        return this._section?._reportsCircle || {
            diameter: Metrics.WIDTH * 2,
            ...DEFAULT_CIRCLE,
        };
    }

    drawHook() {
        if (!this._card) return;

        this.move();
    }

    resetHook() {
        if (!this._card) return;

        this.setupLimits();
    }

    showInfo() {
        if (this.isInfoShow) return;
        this.isInfoShow = true;
        const dir = this.direction || this._section?._reportsDirection || 1;
        this._section?._reportsChrome?.show(this, this._reportData, dir);
        const { paletteDelay } = this._section?._reportsFrame?.circle || this._circle();
        ScrollItem__WidgetReports.schedulePalette(this._palette, paletteDelay);
    }

    hideInfo() {
        if (!this.isInfoShow) return;
        this.isInfoShow = false;
        // Keep chrome until the next item activates (no hide on orbit)
    }

    /** Apply pose; skip DOM writes that did not change */
    _paintCards(__pose, __transform, __transformDeco, __scale) {
        const { left, top, originX, originY } = __pose;
        const origin = `${originX}px ${originY}px`;
        const t = CSS.transform;

        if (this._lastLeft !== left) {
            this._lastLeft = left;
            this._card.style.left = left;
            if (this._cardDeco) this._cardDeco.style.left = left;
        }
        if (this._lastTop !== top) {
            this._lastTop = top;
            this._card.style.top = top;
            if (this._cardDeco) this._cardDeco.style.top = top;
        }
        if (this._lastOrigin !== origin) {
            this._lastOrigin = origin;
            this._card.style.transformOrigin = origin;
            if (this._cardDeco) this._cardDeco.style.transformOrigin = origin;
        }
        if (this._lastRotate !== __transform) {
            this._lastRotate = __transform;
            this._card.style[t] = __transform;
        }
        if (this._lastScale !== __scale) {
            this._lastScale = __scale;
            if (this._figure) this._figure.style[t] = __scale;
            if (this._figureDeco) this._figureDeco.style[t] = __scale;
        }
        if (this._cardDeco && this._lastRotateDeco !== __transformDeco) {
            this._lastRotateDeco = __transformDeco;
            this._cardDeco.style[t] = __transformDeco;
        }
    }

    move() {
        const frame = this._section?._reportsFrame;
        if (!this._cardW || !frame) return;

        const { circle, mods, angleEff, ty } = frame;
        const { scaleMin, infoShowAt, modScaleFast } = circle;
        const progress = Maths.normalize(-1, 1, this.progressInside);
        const translate = CSS.translate3D(0, ty, 0);
        const transform = `${translate} ${CSS.rotate3D(0, 0, 1, angleEff * progress)}`;
        const transformDeco = `${translate} ${CSS.rotate3D(0, 0, 1, angleEff * (progress - 0.2))}`;

        const z = isSmartphone
            ? Math.max(0, Math.round(400 - Math.abs(progress * 100)))
            : Math.round(progress * 100);
        this._z = z;
        if (this._lastZ !== z) {
            this._lastZ = z;
            this.dom.style.zIndex = String(z);
        }

        if (progress < infoShowAt && progress > -infoShowAt) {
            this.showInfo();
        } else {
            this.hideInfo();
        }

        // Active (progress ≈ 0) = 1; entering/exiting: scaleMin → 1 → scaleMin via |progress|
        let scale = Maths.lerp(scaleMin, 1, 1 - Math.abs(progress));
        scale *= scale;
        scale *= Maths.lerp(1, modScaleFast, mods.scale);

        this._paintCards(this._pose, transform, transformDeco, CSS.scale3D(scale, scale, scale));
    }

    /**
     * Rest pose: card center in widget space at last layout sync.
     * Per-frame motion is translate3d(0, ty) + rotate — left/top stay put.
     *
     * Desktop: circle center = viewport center, shifted left by radius.
     * Mobile:  circle center = viewport horizontal center + bottom (y = HEIGHT).
     */
    setupLimits() {
        if (!this._card || !this._section) return null;

        this._cardW = this._card.offsetWidth;
        this._cardH = this._card.offsetHeight;
        if (!this._cardW || !this._cardH) return null;

        const { diameter } = this._circle();
        const radius = diameter / 2;
        const section = this._section._reportsSection;

        if (section) {
            // Active card sits on the viewport horizontal center
            const left = Metrics.CENTER_X - this._cardW / 2 - section.left;
            const top = Metrics.CENTER_Y - this._cardH / 2 - section.top;
            this._pose.left = `${left}px`;
            this._pose.top = `${top}px`;

            if (isSmartphone) {
                // Pivot at viewport center-x / bottom
                this._pose.originX = this._cardW / 2;
                this._pose.originY = (Metrics.HEIGHT - section.top) - top;
            } else {
                // Pivot left of card center by radius, vertically at card mid
                this._pose.originX = this._cardW / 2 - radius;
                this._pose.originY = this._cardH / 2;
            }
        } else if (isSmartphone) {
            this._pose.originX = this._cardW / 2;
            this._pose.originY = Metrics.HEIGHT / 2 + this._cardH / 2;
        } else {
            this._pose.originX = this._cardW / 2 - radius;
            this._pose.originY = this._cardH / 2;
        }

        this._lastLeft = this._lastTop = this._lastOrigin = null;
        this._paintCards(
            this._pose,
            this._lastRotate || '',
            this._lastRotateDeco || '',
            this._lastScale || ''
        );

        return this._pose;
    }

    dispose() {
        // Do not clear chrome — another item may still own the panels
        super.dispose();
    }
}

Scroll._registerClass('WidgetReports', ScrollItem__WidgetReports);

export default ScrollItem__WidgetReports;
